import { useState } from "react";
import { askAmazonCopilot } from "../../../api/client";
import type { AmzAiResponse, AmzFieldDef, AmzIssue, AmzProductDetail } from "../../../types/amazon";
import {
  codePointLength,
  fmtJst,
  issueFieldKey,
  issueTouchedByDraft,
  summarizeValue,
  truncateText,
} from "../../../lib/amazonContent";
import { Badge, Spinner } from "../AmzUi";
import { SEVERITY_TONE, errorMessage } from "../helpers";
import type { ProductEditor } from "../useProductEditor";

interface IssuesTabProps {
  detail: AmzProductDetail;
  editor: ProductEditor;
  onApply: (fieldKey: string, newValue: string) => void;
  showToast: (msg: string) => void;
}

type FixState = { status: "loading" } | { status: "ready"; res: AmzAiResponse } | { status: "error"; message: string };

function issueId(i: AmzIssue, idx: number): string {
  return `${idx}:${i.code}:${i.attributeNames.join(",")}`;
}

function limitText(def: AmzFieldDef | undefined, issue: AmzIssue): string {
  if (!def) return `Amazonのコード ${issue.code}`;
  const parts: string[] = [];
  if (def.softMax) parts.push(`推奨${def.softMax}文字以内`);
  if (def.maxLength) parts.push(`上限${def.maxLength}文字`);
  if (def.maxItems && def.maxItems > 1) parts.push(`最大${def.maxItems}件`);
  return parts.length ? parts.join(" / ") : `Amazonのコード ${issue.code}`;
}

function AmazonValue({ detail, issue }: { detail: AmzProductDetail; issue: AmzIssue }) {
  const name = issue.attributeNames[0];
  if (!name) return <span className="val">—</span>;
  const src = detail.current ?? detail.draft;
  if (name === "bullet_point") {
    const rows = (src.bullet_point ?? []).map((e) => String((e as { value?: unknown }).value ?? ""));
    return (
      <span className="val">
        {rows.length === 0
          ? "（未設定）"
          : rows.map((r, i) => (
              <span key={i} style={{ display: "block" }}>
                Bullet {i + 1}（{codePointLength(r)}文字）{truncateText(r, 60)}
              </span>
            ))}
      </span>
    );
  }
  const def = detail.fields.fields.find((f) => f.key === name);
  return <span className="val">{truncateText(summarizeValue(src, name, def) || "（未設定）", 240)}</span>;
}

export function IssuesTab({ detail, editor, onApply, showToast }: IssuesTabProps) {
  const [fixes, setFixes] = useState<Record<string, FixState>>({});

  const run = async (issue: AmzIssue, id: string) => {
    const target = issueFieldKey(issue);
    setFixes((f) => ({ ...f, [id]: { status: "loading" } }));
    try {
      const res = await askAmazonCopilot(detail.sku, {
        target,
        instruction: `このIssueを解消して: ${issue.message}`,
        draft: editor.draft,
      });
      setFixes((f) => ({ ...f, [id]: { status: "ready", res } }));
    } catch (e) {
      setFixes((f) => ({ ...f, [id]: { status: "error", message: errorMessage(e) } }));
    }
  };

  const close = (id: string) =>
    setFixes((f) => {
      const next = { ...f };
      delete next[id];
      return next;
    });

  const list: { issue: AmzIssue; id: string; resolved: boolean }[] = [
    ...detail.issues.map((issue, i) => ({ issue, id: issueId(issue, i), resolved: false })),
    ...editor.resolvedIssues.map((issue, i) => ({ issue, id: `r${issueId(issue, i)}`, resolved: true })),
  ];

  return (
    <>
      <div className="amz-subtext">
        Amazonから取得したListings Issues · 最終取得 {detail.registered ? fmtJst(detail.currentSyncedAt) : "—（未登録）"}
      </div>
      {list.length === 0 && <div className="amz-card amz-empty">Issueはありません。</div>}
      {list.map(({ issue, id, resolved }) => {
        const touched = !resolved && issueTouchedByDraft(issue, editor.changes);
        const def = detail.fields.fields.find((f) => f.key === issue.attributeNames[0]);
        const fix = fixes[id];
        const fieldLabel = def?.label ?? issue.attributeNames.join(", ") ?? "—";
        const proposal = fix?.status === "ready" ? fix.res.proposals[0] : undefined;
        return (
          <section key={id} className={`amz-issue ${resolved ? "resolved" : ""}`}>
            <div className="amz-issue-top">
              <div className="amz-issue-main">
                <div className="amz-issue-tags">
                  <Badge tone={SEVERITY_TONE[issue.severity]} mono>
                    {issue.severity}
                  </Badge>
                  <span className="amz-hint">{fieldLabel}</span>
                  {resolved && (
                    <Badge tone="success" size="sm">
                      解消（Amazon反映済み）
                    </Badge>
                  )}
                  {touched && (
                    <Badge tone="accent" size="sm">
                      下書きで修正済み・未反映
                    </Badge>
                  )}
                </div>
                <span className="amz-issue-msg">{issue.message}</span>
              </div>
              {!resolved && !fix && (
                <button type="button" className="amz-btn sm outline" onClick={() => void run(issue, id)}>
                  AIで修正
                </button>
              )}
            </div>
            <div className="amz-kv2">
              <span>Amazon値</span>
              <AmazonValue detail={detail} issue={issue} />
              <span>制限</span>
              <span>{limitText(def, issue)}</span>
            </div>
            {fix && (
              <div className="amz-issue-ai">
                {fix.status === "loading" && (
                  <div className="amz-loading-line">
                    <Spinner />
                    修正案を作成しています…
                  </div>
                )}
                {fix.status === "error" && (
                  <>
                    <div className="amz-form-err">修正案を取得できませんでした: {fix.message}</div>
                    <div className="amz-btn-row">
                      <button type="button" className="amz-btn sm" onClick={() => close(id)}>
                        閉じる
                      </button>
                    </div>
                  </>
                )}
                {fix.status === "ready" && (
                  <>
                    <div className="amz-kv2">
                      <span>原因</span>
                      <span>{fix.res.findings.length ? fix.res.findings.map((f) => f.text).join(" ") : (proposal?.reason ?? issue.message)}</span>
                      <span>修正案</span>
                      <span className="fix">{proposal ? proposal.newValue : fix.res.message}</span>
                      <span>影響範囲</span>
                      <span>
                        {proposal
                          ? `${proposal.label}のみ。適用しても下書きが変わるだけで、Amazonへは「変更を確認」から反映するまで送信されません。`
                          : "変更案がないため、下書きは変わりません。"}
                      </span>
                    </div>
                    <div className="amz-btn-row">
                      <button type="button" className="amz-btn sm" onClick={() => close(id)}>
                        閉じる
                      </button>
                      {proposal && (
                        <button
                          type="button"
                          className="amz-btn sm primary"
                          onClick={() => {
                            onApply(proposal.field, proposal.newValue);
                            close(id);
                            showToast("修正案を下書きに適用しました。「変更を確認」からAmazonへ反映できます。");
                          }}
                        >
                          修正案を適用
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}
